/**
 * Today-view due-actions query (P1-6, AC-8/AC-9/AC-10): produces
 * `DailyPageView.dueActions` -- compact, prioritized objective actions for
 * the daily page (R1's daily-page acceptance criteria, habit-free in Phase
 * 1 per phase-1-mvp.md §1b) -- plus the percentage-ring denominator/numerator
 * (`DailyPageView.totalConsideredToday`/`completedToday`, domain-api.ts).
 *
 * Priority order (domain-api.ts `DueActionView` doc comment): dated-today
 * first, then next-in-plan (lowest position among incomplete actions with no
 * due date or a future due date, preferring a `doing`-status action over a
 * `to_do` one at the same priority level -- R3.10, Phase 5) -- one row per
 * active objective that has anything eligible, contributing at most one
 * action per objective so every active objective with due work is
 * represented rather than one objective flooding the list (AC-8 shows
 * exactly this: two objectives, each contributing one row).
 *
 * "Overdue" (dueDate before today) renders neutral, never alarmed (P1-7,
 * AC-10) -- `isOverdue` is exposed as a plain boolean flag; no severity
 * levels, no separate "urgent" bucket. An overdue action is still
 * "dated-today-or-earlier" for priority purposes: it is due work, first in
 * line same as a dated-today action.
 *
 * Phase 5 gating (ADR-0002 addendum 2026-07-04 D7): only `'active'`-status
 * objectives surface on Today -- draft/on_hold/archived are ALL excluded
 * uniformly (replaces the old archived-only check), AND an active
 * objective's own `surfaceOnToday` toggle must be true. Both functions in
 * this file apply the identical filter, so `totalConsideredToday`'s
 * denominator and `dueActions`' list can never disagree about which
 * objectives are in scope.
 */

import type {
  CompassEvent,
  DueActionView,
  NextUpItemView,
  NextUpView,
  ObjectiveId,
} from '../../contracts';
import {
  buildObjectiveStates,
  computeActionCompletionStatus,
  computeActionDisplayStatus,
  resolveActionDisplayStatus,
  type ActionDraftState,
  type ObjectiveState,
} from '../objectives/state';
import { computeDueHabits } from '../habits/projection';
import { listPriorities } from './priorities';

/** True when an objective's actions are eligible to surface on Today at all (Phase 5, D7). */
function isEligibleForToday(state: ObjectiveState): boolean {
  return state.status === 'active' && state.surfaceOnToday;
}

/**
 * Compute the prioritized due-action list across all active objectives.
 *
 * `limit` caps the result to a small count appropriate for one viewport
 * (domain-api.ts: "capped by the domain layer... ux-designer sets the exact
 * number"). Defaults to a conservative value; callers (the composition root)
 * may override once ux-designer's screen spec fixes the real number.
 *
 * Same-day-completed rows STAY in the list (owner bug report: "we should
 * revert back, the action should be there as completed"; design/screens/
 * today-view.md SS1.4/SS5 -- due-today rows and the next-up card both
 * support revert-by-re-tap, matching ActionView's undo pattern). Only a
 * completion from a PRIOR day is treated as genuinely done and excluded --
 * a same-day completion is eligible exactly like any other action for THIS
 * objective's one row, cross-referenced by `completedOn` (not mere presence
 * in the completion map, which would exclude same-day completions too).
 * `entryEventId` is populated with the real `action.completed` event's ID
 * whenever the row is completed, mirroring `ActionView.entryEventId`, so
 * `revertLogEntry` can actually target it (previously hard-coded null).
 *
 * KNOWN CONTRACT GAP (flagged to product-manager/architect, not
 * worked around here): `DueActionView` has no status/completed field --
 * unlike `DueHabitView`'s `status: 'pending' | 'done' | 'skipped'`. A
 * same-day-completed row is therefore currently indistinguishable from a
 * pending one by shape alone; the UI cannot yet render the completed/filled
 * state or gate the tap target as "re-tap to undo" vs. "tap to complete"
 * from this view. `entryEventId !== null` is the only currently-available
 * signal (the same convention `ActionView`/`DueHabitView` already use: a
 * non-null entryEventId implies "there is a completion event, revertible"),
 * so `entryEventId` is still meaningfully useful today, but a first-class
 * status field is the correct long-term fix and needs an architect-owned
 * contract change to `src/contracts/domain-api.ts`.
 */
export function computeDueActions(
  events: readonly CompassEvent[],
  today: string,
  limit = 5,
): readonly DueActionView[] {
  const states = buildObjectiveStates(events);
  const completion = computeActionCompletionStatus(events);
  const displayStatus = computeActionDisplayStatus(events, completion);

  const candidates: DueActionView[] = [];

  for (const state of states.values()) {
    if (!isEligibleForToday(state)) continue;

    const eligible = state.order
      .map((actionId) => state.actionsById.get(actionId))
      .filter((action): action is ActionDraftState => {
        if (action === undefined || action.removed) return false;
        const status = completion.get(action.actionId);
        // No completion at all -> eligible. Completed TODAY -> still
        // eligible (stays visible, completed). Completed on a prior day ->
        // genuinely done, excluded (matches the pre-existing behavior for
        // anything completed before today).
        return status === undefined || status.completedOn === today;
      });

    if (eligible.length === 0) continue;

    const best = pickBestAction(eligible, today, displayStatus);
    if (!best) continue;

    const status = completion.get(best.action.actionId);
    const isDoneToday = status !== undefined && status.completedOn === today;

    candidates.push({
      objectiveId: state.objectiveId,
      objectiveTitle: state.title,
      actionId: best.action.actionId,
      title: best.action.title,
      dueDate: best.action.dueDate as DueActionView['dueDate'],
      isOverdue: best.action.dueDate !== null && best.action.dueDate < today,
      status: isDoneToday ? 'done' : 'pending',
      entryEventId: (status?.entryEventId ?? null) as DueActionView['entryEventId'],
    });
  }

  const sorted = sortDueActions(candidates, today);
  return sorted.slice(0, limit);
}

export interface DailyProgress {
  /** Count of eligible today-or-next-up actions, one per active objective. */
  readonly totalConsideredToday: number;
  /** Of `totalConsideredToday`, how many were completed today. */
  readonly completedToday: number;
}

/**
 * The percentage-ring denominator/numerator (`DailyPageView
 * .totalConsideredToday`/`completedToday`, domain-api.ts). Uses the SAME
 * per-objective priority logic as `computeDueActions` (`pickBestAction`:
 * prefer the earliest dated-today-or-overdue action, else the earliest
 * future-dated one, else the lowest-position undated one, preferring
 * `doing` over `to_do` among undated candidates -- Phase 5) -- but run over
 * EVERY non-removed action in the objective regardless of completion
 * (including actions completed on a PRIOR day, unlike `computeDueActions`,
 * which now excludes those as genuinely done -- see that function's own
 * doc comment), and evaluated BEFORE the compact list's viewport/limit cap.
 *
 * This means the two functions can select a different "considered" action
 * for the same objective in one narrow case: an objective with an
 * undated action completed on a prior day ordered BEFORE an undated
 * pending action in plan order. `computeDueActions` skips the prior-day
 * completion and surfaces the pending one; this function's tie-break still
 * sees the prior-day-completed action first and picks it. Both agree on the
 * outcome that matters (the objective is NOT done today, since the chosen
 * action's `completedOn` -- if any -- is not `today`), just not necessarily
 * on which literal action is "the one" for that objective at that moment.
 * Reconciling this fully would mean excluding prior-day completions here
 * too, which was not part of the requested same-day-completion fix and is
 * left as a known, narrow follow-up rather than guessed at.
 *
 * One objective contributes at most one to `totalConsideredToday`, mirroring
 * `computeDueActions`'s "one row per objective" shape -- an objective with
 * zero eligible actions (no actions at all, or all removed) contributes
 * nothing, matching how it would not appear in `dueActions` either. Phase 5:
 * an objective that is not `'active'` or has `surfaceOnToday` off
 * contributes nothing either (same `isEligibleForToday` gate as
 * `computeDueActions`, D7).
 *
 * Bug fix (owner's 4th footer/ring report -- "Gym" showed 3/3-done-for-the-
 * week and completed today, but the header ring still read 0%): this
 * function originally only iterated objective actions. `DailyPageView
 * .habits` was added to the Today view in a LATER round (phase-2b-habits.md)
 * but nothing ever wired due-today habits into this denominator/numerator --
 * so any day with only habits due (no due actions) always rendered a stuck
 * 0% no matter how much got completed. Fixed by folding in the SAME set
 * `computeDueHabits` already returns for `DailyPageView.habits` (the
 * established due-today definition used by the "Due today"/"Keeping in
 * check" sections) -- both build and avoid habits -- rather than
 * re-implementing habit due-ness here. A build habit counts as considered
 * whenever it appears in that list at all (it only appears there while due
 * -- see that function's own AC-B8 doc comment on dropping off once the
 * weekly target is met) and as completed when its status is 'done'; an
 * avoid habit counts as considered whenever it is due WITH remaining
 * allowance (mirrors the Today view's own "Due today" inclusion rule for
 * avoid habits -- an over-allowance habit is "reference only" in Keeping in
 * Check, not part of what today asks of you) and as completed when its
 * status is 'clean'.
 */
export function computeDailyProgress(
  events: readonly CompassEvent[],
  today: string,
): DailyProgress {
  const states = buildObjectiveStates(events);
  const completion = computeActionCompletionStatus(events);
  const displayStatus = computeActionDisplayStatus(events, completion);

  let totalConsideredToday = 0;
  let completedToday = 0;

  for (const state of states.values()) {
    if (!isEligibleForToday(state)) continue;

    const chosen = pickConsideredAction(state, today, displayStatus);
    if (!chosen) continue;

    totalConsideredToday += 1;

    const status = completion.get(chosen.actionId);
    if (status !== undefined && status.completedOn === today) {
      completedToday += 1;
    }
  }

  for (const habit of computeDueHabits(events, today)) {
    if (habit.habitKind === 'build') {
      totalConsideredToday += 1;
      if (habit.status === 'done') completedToday += 1;
    } else {
      // Avoid habits already over their weekly allowance still render (for
      // reference, "Keeping in check") but are not part of what today asks
      // of you -- excluded from both denominator and numerator, matching
      // the Today view's own "Due today" section membership rule.
      if (habit.status !== 'pending' || habit.allowance.used < habit.allowance.limit) {
        totalConsideredToday += 1;
        if (habit.status === 'clean') completedToday += 1;
      }
    }
  }

  return { totalConsideredToday, completedToday };
}

/**
 * The single action one objective would contribute to `totalConsideredToday`
 * -- same priority logic as `pickBestAction`, but over ALL non-removed
 * actions (completed or not), so a fully-completed objective still resolves
 * to the action that "would have been due" rather than nothing. Ties are
 * broken identically to `pickBestAction` (earliest date wins; among
 * undated actions, `doing`-over-`to_do` then plan order via `state.order`
 * wins) so a completed and an incomplete action at the same priority level
 * resolve deterministically -- `state.order` is walked in order and
 * `pickBestAction` takes the first undated candidate it sees, so this
 * shares that determinism for free.
 */
function pickConsideredAction(
  state: ObjectiveState,
  today: string,
  displayStatus: ReturnType<typeof computeActionDisplayStatus>,
): ActionDraftState | null {
  const eligible = state.order
    .map((actionId) => state.actionsById.get(actionId))
    .filter((action): action is ActionDraftState => action !== undefined && !action.removed);

  if (eligible.length === 0) return null;

  return pickBestAction(eligible, today, displayStatus)?.action ?? null;
}

/**
 * Pick the single best action to surface for one objective: prefer the
 * earliest dated action that is due today or overdue; else the earliest
 * future-dated action; else the lowest-position undated action
 * (next-in-plan), preferring one whose resolved status is `'doing'` over a
 * `'to_do'` one at that same undated tier (R3.10, Phase 5, ADR-0002 addendum
 * 2026-07-04 D3) -- this only ever affects the undated branch; dated
 * candidates are unaffected. This keeps each objective contributing exactly
 * one row to the Today view (AC-8's shape), with the objective's own
 * internal ordering (plus the doing-preference tie-break) deciding which
 * one.
 *
 * Generic over `ActionDraftState` (a strict superset of what this needs --
 * `actionId`, `title`, `dueDate`, `milestoneTag`): shared verbatim by
 * `computeDueActions` (called with only-incomplete actions) and
 * `computeDailyProgress` (called with ALL non-removed actions, completed or
 * not) so both use identical priority/tie-break logic -- the coordinator's
 * requirement that `totalConsideredToday` use "the same selection logic as
 * dueActions".
 */
function pickBestAction(
  candidates: readonly ActionDraftState[],
  today: string,
  displayStatus: ReturnType<typeof computeActionDisplayStatus>,
): { readonly action: ActionDraftState } | null {
  if (candidates.length === 0) return null;

  let dueOrOverdue: ActionDraftState | null = null;
  let futureDated: ActionDraftState | null = null;
  let firstUndated: ActionDraftState | null = null;
  let firstUndatedDoing: ActionDraftState | null = null;

  for (const action of candidates) {
    if (action.dueDate !== null && action.dueDate <= today) {
      if (
        dueOrOverdue === null ||
        action.dueDate < (dueOrOverdue.dueDate as string)
      ) {
        dueOrOverdue = action;
      }
    } else if (action.dueDate !== null) {
      if (futureDated === null || action.dueDate < (futureDated.dueDate as string)) {
        futureDated = action;
      }
    } else {
      if (firstUndated === null) firstUndated = action;
      if (
        firstUndatedDoing === null &&
        resolveActionDisplayStatus(displayStatus, action.actionId) === 'doing'
      ) {
        firstUndatedDoing = action;
      }
    }
  }

  // Phase 5 (D3): among undated candidates, a 'doing' action is preferred
  // over the plan-order-first 'to_do' one -- but never leapfrogs a
  // dated-today/overdue or future-dated action, which are decided above and
  // take priority regardless.
  const chosen = dueOrOverdue ?? firstUndatedDoing ?? firstUndated ?? futureDated;
  return chosen ? { action: chosen } : null;
}

/**
 * Final cross-objective ordering: dated-today-or-overdue rows first (earliest
 * due date first), then next-in-plan rows (undated or future-dated) in a
 * stable, deterministic order (by objectiveId, since plan position is
 * already resolved per-objective by `pickBestAction`).
 */
function sortDueActions(
  candidates: readonly DueActionView[],
  today: string,
): readonly DueActionView[] {
  const dueGroup = candidates.filter(
    (c) => c.dueDate !== null && c.dueDate <= today,
  );
  const nextUpGroup = candidates.filter(
    (c) => c.dueDate === null || c.dueDate > today,
  );

  dueGroup.sort((a, b) => compareDueDate(a.dueDate, b.dueDate));
  nextUpGroup.sort((a, b) => compareObjectiveId(a.objectiveId, b.objectiveId));

  return [...dueGroup, ...nextUpGroup];
}

function compareDueDate(a: string | null, b: string | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareObjectiveId(a: ObjectiveId, b: ObjectiveId): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * "Next up · tomorrow" preview (§5, PRD §9 time-of-day ruling, Phase 6) --
 * a date-only, ORDERED preview of tomorrow's Priorities and next-in-plan
 * actions. Explicitly NOT a scheduling query (contract doc comment): nothing
 * about tomorrow's actual data changes by appearing here.
 *
 * Ordering: (a) items WITH an explicit `timeLabel`, sorted by that label
 * (`HH:mm` string-sorts correctly); then (b) items with no time label, using
 * `computeDueActions`'s existing due-today/next-in-plan selection (run
 * against `tomorrow` as the reference date) for the action half, and
 * Priorities' own creation order for the Priority half -- both halves of (b)
 * interleave in a stable, deterministic order (Priorities before actions at
 * the same "no time label" tier, since Priorities are today's/tomorrow's
 * most concrete commitments per the spec's own card ordering, §7).
 */
export function getNextUp(
  events: readonly CompassEvent[],
  tomorrow: string,
): NextUpView {
  const timedItems: NextUpItemView[] = [];
  const untimedItems: NextUpItemView[] = [];

  for (const priority of listPriorities(events, tomorrow)) {
    const item: NextUpItemView = {
      kind: 'priority',
      id: priority.priorityId,
      title: priority.title,
      timeLabel: priority.timeLabel,
      objectiveTitle: null,
    };
    (priority.timeLabel !== null ? timedItems : untimedItems).push(item);
  }

  // Actions have no time label concept (DisplayTimeLabel lives only on
  // Priority) -- every action-sourced item is untimed by construction, using
  // the same next-in-plan selection Today's own dueActions uses, evaluated
  // AS OF tomorrow (so "due tomorrow" reads as "due today" from tomorrow's
  // vantage point, matching computeDueActions's own date-relative logic).
  for (const action of computeDueActions(events, tomorrow, Number.MAX_SAFE_INTEGER)) {
    untimedItems.push({
      kind: 'action',
      id: action.actionId,
      title: action.title,
      timeLabel: null,
      objectiveTitle: action.objectiveTitle,
    });
  }

  timedItems.sort((a, b) => (a.timeLabel! < b.timeLabel! ? -1 : a.timeLabel! > b.timeLabel! ? 1 : 0));

  return {
    items: [...timedItems, ...untimedItems],
    draft: true,
  };
}
